/**
 * One root team of the Cards tab (Figma `315:11169`) as its own card: a team's head row --
 * level badge, name, the five figures the structure read now carries per team
 * (`owner-decisions-align-per-team-figures-have-no-producer`), and its `⋯` menu -- collapsible
 * over its child teams and, once expanded, its own directly assigned accounts (`315-11169-01`'s
 * leaf rows).
 *
 * `reach` has no producer anywhere in the platform yet (`schemas.ts`'s own note) and renders
 * through `Absent` rather than a number. Account rows carry only what
 * `trackedAccountRowSchema` actually has (identity and platform): the frame also draws a
 * per-account reach bar, posts count and sentiment chip, but no route serves those figures per
 * account, and this task does not invent them.
 */
import { useState, type ReactNode } from 'react';
import { ChevronRight, Eye, Newspaper, Users } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { HintedIcon } from '../../../chrome/hint';
import { Absent } from '../../../chrome/measure';
import { initials } from '../../../chrome/shell/initials';
import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Badge } from '../../../ui/badge';
import { AccountAvatar } from '../../data/media/AccountAvatar';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import { Chip } from '../../../ui/chip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/tooltip';
import { useTrackedAccounts } from '../accounts/api/queries';
import type { TrackedAccountRow } from '../accounts/api/schemas';
import { platformLabel, PlatformIcon } from '../accounts/NoSelection';
import { ACCOUNT_PATH } from '../accounts/paths';
import { t } from '../i18n';
import { TeamActions } from './TeamActions';
import type { TeamFiguresOut } from './schemas';

export type TeamCardNode = TeamFiguresOut & { children: TeamCardNode[] };

/** The flat `teams` array as each one's own subtree, by `parent_team_id` -- same construction as
 *  `Tree.tsx`'s `buildTeamTree`, kept as its own copy here rather than imported: the two views
 *  are worked on in parallel branches and neither is the other's dependency. */
export function buildTeamCardTree(teams: TeamFiguresOut[]): TeamCardNode[] {
  const nodeMap = new Map<string, TeamCardNode>();
  for (const team of teams) {
    nodeMap.set(team.id, { ...team, children: [] });
  }
  const roots: TeamCardNode[] = [];
  for (const team of teams) {
    const node = nodeMap.get(team.id)!;
    if (team.parent_team_id && nodeMap.has(team.parent_team_id)) {
      nodeMap.get(team.parent_team_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function signedInteger(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { signDisplay: 'exceptZero', maximumFractionDigits: 0 })
    .format(value)
    .replace(/^-/, '−');
}

function SentimentChip(props: { value: number }): ReactNode {
  const locale = getLocale();
  const tone = props.value > 0 ? 'positive' : props.value < 0 ? 'negative' : 'neutral';
  return (
    <Chip tone={tone} size="sm">
      <span className="tabular-nums">{signedInteger(props.value, locale)}</span>
    </Chip>
  );
}

/** Each figure is an icon and a number, so its name and what it counts sit in a tooltip, reachable
 *  by keyboard as well as by pointer; the name alone is also the figure's accessible text. */
function Figure(props: { label: string; hint: string; children: ReactNode }): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="flex cursor-default items-center gap-1 rounded-sm text-caption text-muted-foreground outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="sr-only">{props.label} </span>
          {props.children}
        </button>
      </TooltipTrigger>
      <TooltipContent className="flex flex-col gap-0.5">
        <span className="font-medium">{props.label}</span>
        <span>{props.hint}</span>
      </TooltipContent>
    </Tooltip>
  );
}

function FiguresRow(props: { team: TeamFiguresOut }): ReactNode {
  const locale = getLocale();
  const { team } = props;
  const iconClass = 'size-3.5';

  return (
    <TooltipProvider>
      <div className="flex flex-wrap items-center gap-4">
        {team.lead_identity !== null && (
          <Figure label={t('teams.card.lead.label')} hint={t('teams.card.lead.hint')}>
            <Avatar size="sm">
              <AvatarFallback>{initials(team.lead_identity)}</AvatarFallback>
            </Avatar>
            <span className="text-body-sm text-foreground">{team.lead_identity}</span>
          </Figure>
        )}
        <Figure label={t('teams.card.figure.members')} hint={t('teams.card.figure.members.hint')}>
          <Users aria-hidden className={iconClass} />
          <span className="tabular-nums">{integer(team.members, locale)}</span>
        </Figure>
        <Figure label={t('teams.card.figure.reach')} hint={t('teams.card.figure.reach.hint')}>
          <Eye aria-hidden className={iconClass} />
          {team.reach === null ? (
            <Absent reason="not_observable" />
          ) : (
            <span className="tabular-nums">{integer(team.reach, locale)}</span>
          )}
        </Figure>
        <Figure label={t('teams.card.figure.posts')} hint={t('teams.card.figure.posts.hint')}>
          <Newspaper aria-hidden className={iconClass} />
          <span className="tabular-nums">{integer(team.posts, locale)}</span>
        </Figure>
        <Figure
          label={t('teams.card.figure.sentiment')}
          hint={t('teams.card.figure.sentiment.hint')}
        >
          <SentimentChip value={team.sentiment_delta} />
        </Figure>
      </div>
    </TooltipProvider>
  );
}

function AccountRow(props: { campaignId: string; account: TrackedAccountRow }): ReactNode {
  const { campaignId, account } = props;
  const label = account.label ?? account.handle ?? account.id;
  return (
    <Link
      to={ACCOUNT_PATH}
      params={{ campaignId, accountId: account.id }}
      className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-accent"
    >
      <span className="flex min-w-0 items-center gap-2">
        <AccountAvatar size="sm" avatarUrl={account.avatar_url} name={label} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-body-sm font-medium text-foreground">{label}</span>
          <span className="flex items-center gap-1 text-caption text-muted-foreground">
            <HintedIcon label={platformLabel(account.platform)}>
              <PlatformIcon platform={account.platform} className="size-3" />
            </HintedIcon>
            {account.handle !== null && `@${account.handle}`}
          </span>
        </span>
      </span>
      <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}

function AccountsSection(props: { campaignId: string; team: TeamFiguresOut }): ReactNode {
  const { campaignId, team } = props;
  const query = useTrackedAccounts(campaignId, { team_id: team.id });

  if (query.status === 'pending') {
    return (
      <p role="status" className="px-2 py-1.5 text-caption text-muted-foreground">
        {t('teams.card.accounts.loading')}
      </p>
    );
  }
  if (query.status === 'error') {
    return (
      <p role="alert" className="px-2 py-1.5 text-caption text-destructive">
        {t('teams.card.accounts.error')}
      </p>
    );
  }
  return (
    <div
      role="group"
      aria-label={t('teams.card.accounts.label', { name: team.name })}
      className="flex flex-col gap-0.5"
    >
      {query.data.map((account) => (
        <AccountRow key={account.id} campaignId={campaignId} account={account} />
      ))}
    </div>
  );
}

/** One team and, once expanded, what sits inside it. Child teams are rows indented under their
 *  parent, divided by a hairline, never a card inside a card. */
function TeamBranch(props: { campaignId: string; node: TeamCardNode }): ReactNode {
  const { campaignId, node } = props;
  const [expanded, setExpanded] = useState(false);
  const expandable = node.children.length > 0 || node.direct_accounts > 0;

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      data-slot="team-card"
      data-team-id={node.id}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {expandable ? (
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6 shrink-0"
                aria-label={
                  expanded
                    ? t('teams.collapse', { name: node.name })
                    : t('teams.expand', { name: node.name })
                }
              >
                <ChevronRight
                  aria-hidden
                  className={`size-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
                />
              </Button>
            </CollapsibleTrigger>
          ) : (
            <span className="inline-block size-6 shrink-0" aria-hidden="true" />
          )}
          <Badge variant="outline" className="shrink-0">
            {node.level_name}
          </Badge>
          <span className="truncate font-medium text-foreground">{node.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <FiguresRow team={node} />
          <TeamActions campaignId={campaignId} team={node} />
        </div>
      </div>

      {/* No `animate-*` class here, so Radix's own `Presence` (which keys off
       *  `getComputedStyle(node).animationName`, never `transitionDuration`) unmounts this
       *  synchronously on close and never mounts before `open` -- the accounts fetch already
       *  fires only on expand with no extra gate. Giving this a CSS animation later reopens a
       *  transition window and needs a manual `expanded` gate restored alongside it. */}
      {expandable && (
        <CollapsibleContent className="pl-6">
          {node.children.map((child) => (
            <div key={child.id} className="border-t">
              <TeamBranch campaignId={campaignId} node={child} />
            </div>
          ))}
          {node.direct_accounts > 0 && (
            <div className="border-t px-4 py-2">
              <AccountsSection campaignId={campaignId} team={node} />
            </div>
          )}
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

export function TeamCard(props: { campaignId: string; node: TeamCardNode }): ReactNode {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <TeamBranch campaignId={props.campaignId} node={props.node} />
    </Card>
  );
}
